/**
 * Dashboard page — shows overview cards for all portfolio companies.
 * In live mode: requires authentication via MsalAuthenticationTemplate.
 * In demo mode: loads sample data without authentication.
 *
 * Executive features (toggleable via settings):
 * - Portfolio Scoreboard: aggregate metrics across all companies
 * - Sparklines: mini trend charts on each card
 * - Animated Numbers: KPI count-up animation on load
 */

import { Link } from "react-router-dom";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { powerbiRequest } from "../authConfig";
import { Loading } from "../ui-components/Loading";
import { ErrorComponent } from "../ui-components/ErrorComponent";
import { ProgressBar } from "../ui-components/ProgressBar";
import { RefreshButton } from "../ui-components/RefreshButton";
import { isDemoMode } from "../utils/PowerBIApiCall";
import type {
  PortcoListItem,
  PortcoMetrics,
  DailyTrendPoint,
} from "../utils/PowerBIApiCall";
import { useDashboardData } from "../hooks/useQueries";
import { useRefreshData } from "../hooks/useRefreshData";
import { PortfolioScoreboard } from "../ui-components/PortfolioScoreboard";
import {
  DashboardSettings,
  useDashboardFeatures,
} from "../ui-components/DashboardSettings";
import { CalcTooltip } from "../ui-components/CalcTooltip";
import { useAnimatedNumber } from "../hooks/useAnimatedNumber";
import {
  buildForecast,
  buildPeriodForecast,
  attainmentRate,
} from "../utils/forecastModel";
import { ResponsiveContainer, BarChart, Bar, Cell } from "recharts";
import { formatCurrency, formatPctSigned as formatPct } from "../utils/format";
import { SERIES } from "../utils/chartTheme";

function getColorClass(value: number): string {
  return value >= 0 ? "text-emerald-600" : "text-red-600";
}

/** Animated currency display — counts up from 0 when enabled */
function AnimatedCurrency({
  value,
  animate,
}: {
  value: number;
  animate: boolean;
}) {
  const displayed = useAnimatedNumber(value, animate ? 1200 : 0);
  return <>{formatCurrency(displayed)}</>;
}

/** Animated percentage display */
function AnimatedPctValue({
  value,
  animate,
}: {
  value: number;
  animate: boolean;
}) {
  const displayed = useAnimatedNumber(value, animate ? 1200 : 0);
  return <>{formatPct(displayed)}</>;
}

/** Performance indicator dot */
function PacingDot({ pct }: { pct: number }) {
  const color =
    pct >= 100 ? "bg-emerald-500" : pct >= 90 ? "bg-amber-500" : "bg-red-500";
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

/** Pacing label */
function PacingLabel({ pct }: { pct: number }) {
  if (pct >= 100)
    return (
      <span className="text-emerald-600 text-[10px] font-semibold">
        On Track
      </span>
    );
  if (pct >= 90)
    return (
      <span className="text-amber-600 text-[10px] font-semibold">At Risk</span>
    );
  return <span className="text-red-600 text-[10px] font-semibold">Behind</span>;
}

/** Mini bar chart for dashboard cards — shows daily sales trend */
function CardBarChart({ data }: { data: DailyTrendPoint[] }) {
  if (!data || data.length < 2) return null;

  const lastIndex = data.length - 1;

  return (
    <div style={{ width: "100%", height: 48 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
          <Bar
            dataKey="dailySales"
            radius={[2, 2, 0, 0]}
            isAnimationActive={true}
            animationDuration={800}
          >
            {data.map((_, i) => (
              <Cell key={i} fill={i === lastIndex ? "#16a34a" : "#bbf7d0"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// =============================================================================
// Shared UI — pure presentational component, no MSAL dependency
// =============================================================================

interface DashboardUIProps {
  portcos: PortcoListItem[];
  metrics: Record<string, PortcoMetrics>;
  loading: boolean;
  progress: { loaded: number; total: number };
  settledIds: string[];
  error: string | null;
  demo: boolean;
}

/** Skeleton placeholder for a single portco card — shown until that card's data settles */
function PortcoCardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-card border border-gray-200/70 overflow-hidden">
      <div className="h-12 bg-gray-50 animate-pulse" />
      <div className="p-5 space-y-3">
        <div className="h-4 bg-gray-100 rounded animate-pulse w-3/4" />
        <div className="h-7 bg-gray-100 rounded animate-pulse w-1/2" />
        <div className="h-3 bg-gray-50 rounded animate-pulse" />
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="h-16 bg-gray-50 rounded-lg animate-pulse" />
          <div className="h-16 bg-gray-50 rounded-lg animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function DashboardUI({
  portcos,
  metrics,
  loading,
  progress,
  settledIds,
  error,
  demo,
}: DashboardUIProps) {
  const { features, toggle, resetAll } = useDashboardFeatures();
  const { refresh, refreshing } = useRefreshData();

  if (error) {
    return (
      <div className="w-full max-w-lg">
        <div className="bg-white border border-red-100 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-4 h-4 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-1">
                  Error Loading Dashboard
                </h3>
                <p className="text-gray-500 text-sm">{error}</p>
                <p className="text-gray-400 text-xs mt-3">
                  Make sure the backend server is running on port 4000
                  {!demo && " and your Azure AD App Registration is configured"}
                  .
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const hasMetrics = Object.keys(metrics).length > 0;
  const animate = features.animatedNumbers;
  const progressPct =
    progress.total > 0 ? (progress.loaded / progress.total) * 100 : 0;

  return (
    <div className="w-full max-w-6xl">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Portfolio Dashboard
          </h1>
          {loading ? (
            <div className="mt-1.5 max-w-xs">
              <p className="text-gray-400 text-sm">
                {progress.total > 0
                  ? `Loading company data — ${progress.loaded} of ${progress.total}`
                  : "Loading portfolio list…"}
              </p>
              <div className="mt-2">
                <ProgressBar pct={progressPct} color={SERIES.primary} />
              </div>
            </div>
          ) : (
            <p className="text-gray-400 text-sm mt-1">
              Daily financial performance across all portfolio companies
              {demo && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 uppercase tracking-wider">
                  Demo
                </span>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RefreshButton onClick={refresh} refreshing={refreshing} />
          <DashboardSettings
            features={features}
            onToggle={toggle}
            onReset={resetAll}
          />
        </div>
      </div>

      {/* Portfolio Scoreboard — only once every portco has settled, so
          aggregates never display as complete while still partial */}
      {features.scoreboard && !loading && hasMetrics && (
        <div className="opacity-0 animate-fade-in stagger-1">
          <PortfolioScoreboard metrics={metrics} animate={animate} />
        </div>
      )}
      {features.scoreboard && loading && (
        <div className="bg-white rounded-xl shadow-card border border-gray-200/70 overflow-hidden mb-6">
          <div className="h-24 bg-gray-50 animate-pulse" />
        </div>
      )}

      {/* Portco Cards Grid — each slot shows a skeleton until that specific
          portco's metrics settle, so cards populate as data actually arrives */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {portcos.length === 0 &&
          loading &&
          [1, 2, 3].map((i) => <PortcoCardSkeleton key={i} />)}
        {portcos.map((portco, index) => {
          if (!settledIds.includes(portco.id)) {
            return <PortcoCardSkeleton key={portco.id} />;
          }
          const m = metrics[portco.id];
          const mtdVsBudgetPct = m ? m.mtd.varianceVsBudgetPct : 0;
          const ytdVsBudgetPct = m ? m.ytd.varianceVsBudgetPct : 0;
          const mtdPacing =
            m && m.mtd.budgetProrated > 0
              ? (m.mtd.actual / m.mtd.budgetProrated) * 100
              : 0;

          // Regression month-end forecast (throughput only — no open orders).
          const fcst =
            m && m.dailyTrend && m.dailyTrend.length > 2
              ? buildForecast(m.dailyTrend, m.mtd, m.mtd.budgetFull)
              : null;
          const mtdFcstVsBgt =
            fcst && m.mtd.budgetFull > 0
              ? ((fcst.summary.forecastTotal - m.mtd.budgetFull) /
                  m.mtd.budgetFull) *
                100
              : undefined;
          // YTD: rest of this month from the regression, future months from
          // their own budgets scaled by year-to-date attainment.
          const ytdForecastTotal =
            fcst && m
              ? buildPeriodForecast(
                  m.ytd,
                  m.mtd,
                  fcst.summary.forecastTotal,
                  attainmentRate(m.ytd),
                )
              : 0;
          const ytdFcstVsBgt =
            fcst && m && m.ytd.budgetFull > 0
              ? ((ytdForecastTotal - m.ytd.budgetFull) / m.ytd.budgetFull) * 100
              : undefined;

          return (
            <Link
              key={portco.id}
              to={`/portco/${portco.id}`}
              className="no-underline opacity-0 animate-fade-in-up"
              style={{ animationDelay: `${0.15 + index * 0.08}s` }}
            >
              <div className="group bg-white rounded-xl shadow-card border border-gray-200/70 overflow-hidden transition-all duration-300 hover:shadow-hover hover:border-gray-300/70 hover:-translate-y-0.5 cursor-pointer h-full">
                {/* Card Header — compact with pacing indicator */}
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {m && <PacingDot pct={mtdPacing} />}
                    <h2 className="text-[15px] font-semibold text-gray-900 truncate">
                      {portco.displayName}
                    </h2>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {m && <PacingLabel pct={mtdPacing} />}
                    <svg
                      className="w-4 h-4 text-gray-300 group-hover:text-wmp-500 transition-colors"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5">
                  {m ? (
                    <>
                      {/* Daily Sales — hero metric */}
                      <div className="mb-4">
                        <div className="flex items-baseline justify-between mb-1">
                          <p className="microlabel">Daily Sales</p>
                          {m.reportDateFormatted && (
                            <p className="text-[10px] text-gray-400">
                              {m.reportDateFormatted}
                            </p>
                          )}
                        </div>
                        <p className="text-2xl font-bold text-gray-900 tracking-tight tabular-nums">
                          {animate ? (
                            <AnimatedCurrency
                              value={m.daily.dailySales}
                              animate={animate}
                            />
                          ) : (
                            formatCurrency(m.daily.dailySales)
                          )}
                        </p>
                        {(() => {
                          if (
                            !m.dailyTrend ||
                            m.dailyTrend.length < 2 ||
                            m.daily.dailySales === 0
                          )
                            return null;
                          const trendSales = m.dailyTrend
                            .slice(0, -1)
                            .map((d) => d.dailySales)
                            .filter((v) => v > 0);
                          if (trendSales.length === 0) return null;
                          const avg =
                            trendSales.reduce((a, b) => a + b, 0) /
                            trendSales.length;
                          if (avg === 0) return null;
                          const vsAvgPct =
                            ((m.daily.dailySales - avg) / avg) * 100;
                          const isUp = vsAvgPct >= 0;
                          return (
                            <CalcTooltip
                              formula="(Today − Avg) ÷ Avg"
                              values={`= (${formatCurrency(m.daily.dailySales)} − ${formatCurrency(avg)}) ÷ ${formatCurrency(avg)}`}
                            >
                              <div className="flex items-center gap-1.5 mt-1">
                                <span
                                  className={`inline-flex items-center gap-0.5 text-xs font-bold ${getColorClass(vsAvgPct)}`}
                                >
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2.5}
                                      d={
                                        isUp
                                          ? "M5 15l7-7 7 7"
                                          : "M19 9l-7 7-7-7"
                                      }
                                    />
                                  </svg>
                                  {formatPct(vsAvgPct)}
                                </span>
                                <span className="text-[11px] text-gray-400">
                                  vs {formatCurrency(avg)} avg
                                </span>
                              </div>
                            </CalcTooltip>
                          );
                        })()}
                      </div>

                      {/* Daily bar chart */}
                      {features.sparklines &&
                        m.dailyTrend &&
                        m.dailyTrend.length > 2 && (
                          <div className="mb-4 -mx-1">
                            <CardBarChart data={m.dailyTrend} />
                          </div>
                        )}

                      {/* Current Pace */}
                      <p className="microlabel mb-1.5">Current Pace</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50/80 rounded-lg p-3">
                          <p className="text-[10px] font-medium text-gray-400">
                            MTD
                          </p>
                          <p className="text-sm font-bold text-gray-800 tabular-nums mt-0.5">
                            {animate ? (
                              <AnimatedCurrency
                                value={m.mtd.actual}
                                animate={animate}
                              />
                            ) : (
                              formatCurrency(m.mtd.actual)
                            )}
                          </p>
                          <CalcTooltip
                            formula="(Actual − Budget Pro) ÷ Budget Pro — open orders excluded"
                            values={`= (${formatCurrency(m.mtd.actual)} − ${formatCurrency(m.mtd.budgetProrated)}) ÷ ${formatCurrency(m.mtd.budgetProrated)}`}
                          >
                            <p
                              className={`text-[11px] font-semibold mt-0.5 ${getColorClass(
                                mtdVsBudgetPct,
                              )}`}
                            >
                              {animate ? (
                                <AnimatedPctValue
                                  value={mtdVsBudgetPct}
                                  animate={animate}
                                />
                              ) : (
                                formatPct(mtdVsBudgetPct)
                              )}{" "}
                              <span className="text-gray-400 font-normal">
                                vs bgt
                              </span>
                            </p>
                          </CalcTooltip>
                        </div>
                        <div className="bg-gray-50/80 rounded-lg p-3">
                          <p className="text-[10px] font-medium text-gray-400">
                            YTD
                          </p>
                          <p className="text-sm font-bold text-gray-800 tabular-nums mt-0.5">
                            {animate ? (
                              <AnimatedCurrency
                                value={m.ytd.actual}
                                animate={animate}
                              />
                            ) : (
                              formatCurrency(m.ytd.actual)
                            )}
                          </p>
                          <CalcTooltip
                            formula="(Actual − Budget Pro) ÷ Budget Pro — open orders excluded"
                            values={`= (${formatCurrency(m.ytd.actual)} − ${formatCurrency(m.ytd.budgetProrated)}) ÷ ${formatCurrency(m.ytd.budgetProrated)}`}
                          >
                            <p
                              className={`text-[11px] font-semibold mt-0.5 ${getColorClass(
                                ytdVsBudgetPct,
                              )}`}
                            >
                              {animate ? (
                                <AnimatedPctValue
                                  value={ytdVsBudgetPct}
                                  animate={animate}
                                />
                              ) : (
                                formatPct(ytdVsBudgetPct)
                              )}{" "}
                              <span className="text-gray-400 font-normal">
                                vs bgt
                              </span>
                            </p>
                          </CalcTooltip>
                        </div>
                      </div>

                      {/* Forecast */}
                      {features.forecast &&
                        (mtdFcstVsBgt !== undefined ||
                          ytdFcstVsBgt !== undefined) && (
                          <>
                            <p className="microlabel mt-3 mb-1.5">Forecast</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-gray-50/80 rounded-lg p-3">
                                {mtdFcstVsBgt !== undefined ? (
                                  <>
                                    <p className="text-[10px] font-medium text-gray-400">
                                      Full Month
                                    </p>
                                    <p className="text-sm font-bold text-gray-800 tabular-nums mt-0.5">
                                      {formatCurrency(
                                        fcst!.summary.forecastTotal,
                                      )}
                                    </p>
                                    <CalcTooltip
                                      formula="Projected FULL MONTH: (Forecast − Budget Full) ÷ Budget Full"
                                      values={`= (${formatCurrency(fcst!.summary.forecastTotal)} − ${formatCurrency(m.mtd.budgetFull)}) ÷ ${formatCurrency(m.mtd.budgetFull)}`}
                                    >
                                      <p
                                        className={`text-[11px] font-semibold mt-0.5 ${getColorClass(mtdFcstVsBgt)}`}
                                      >
                                        {formatPct(mtdFcstVsBgt)}{" "}
                                        <span className="text-gray-400 font-normal">
                                          vs bgt
                                        </span>
                                      </p>
                                    </CalcTooltip>
                                  </>
                                ) : (
                                  <p className="text-[11px] text-gray-300 py-1">
                                    —
                                  </p>
                                )}
                              </div>
                              <div className="bg-gray-50/80 rounded-lg p-3">
                                {ytdFcstVsBgt !== undefined ? (
                                  <>
                                    <p className="text-[10px] font-medium text-gray-400">
                                      Full Year
                                    </p>
                                    <p className="text-sm font-bold text-gray-800 tabular-nums mt-0.5">
                                      {formatCurrency(ytdForecastTotal)}
                                    </p>
                                    <CalcTooltip
                                      formula="Projected FULL YEAR: (Forecast − Yearly Budget) ÷ Yearly Budget"
                                      values={`= (${formatCurrency(ytdForecastTotal)} − ${formatCurrency(m.ytd.budgetFull)}) ÷ ${formatCurrency(m.ytd.budgetFull)}`}
                                    >
                                      <p
                                        className={`text-[11px] font-semibold mt-0.5 ${getColorClass(ytdFcstVsBgt)}`}
                                      >
                                        {formatPct(ytdFcstVsBgt)}{" "}
                                        <span className="text-gray-400 font-normal">
                                          vs bgt
                                        </span>
                                      </p>
                                    </CalcTooltip>
                                  </>
                                ) : (
                                  <p className="text-[11px] text-gray-300 py-1">
                                    —
                                  </p>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                    </>
                  ) : (
                    <div className="py-6 text-center">
                      <svg
                        className="w-10 h-10 text-gray-200 mx-auto mb-3"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
                      </svg>
                      <p className="text-sm text-gray-400">
                        Configure Power BI dataset
                      </p>
                      <p className="text-xs text-gray-300 mt-1">
                        Click to view details
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Data loader — uses React Query (works in both demo and live mode)
// =============================================================================

function DashboardContent() {
  const { portcos, metrics, loading, progress, settledIds, error } =
    useDashboardData();
  const demo = isDemoMode();

  return (
    <DashboardUI
      portcos={portcos}
      metrics={metrics}
      loading={loading}
      progress={progress}
      settledIds={settledIds}
      error={error}
      demo={demo}
    />
  );
}

// =============================================================================
// Exported Dashboard component — routes to demo or live mode
// =============================================================================

export function Dashboard() {
  if (isDemoMode()) {
    return <DashboardContent />;
  }

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={powerbiRequest}
      errorComponent={ErrorComponent}
      loadingComponent={Loading}
    >
      <DashboardContent />
    </MsalAuthenticationTemplate>
  );
}
